import {
  AssetsPaginatedResponse,
  AssetsResponse,
  CursorState,
  ISO8601DateTimeString,
  ProjectId,
  UnitOfMeasurement,
  UnknownObject,
  UserId,
  UUIDv4,
} from "../types";
import { Asset, AssetDTO, NewAssetDTO } from "./assets";
import { AssetsAsyncApiResponse } from "./asyncJobs";
import { Category } from "./categories";

export type BatchCreateRequest = {
  modelLineageUrn: string;
  metadata?: UnknownObject;
};

export type SyncState = {
  syncedModelVersionUrn: string;
  syncedModelVersionNumber: number;
  syncedModelRevisionNumber: number;
  syncedAt: string;
  syncedBy: UserId;
};

export type ModelSyncContainer = {
  id: UUIDv4;
  createdAt: Date;
  createdBy: UserId;
  updatedAt: Date;
  updatedBy: UserId;
  deletedAt?: Date;
  deletedBy?: UserId;
  isActive: boolean;
  version: number;
  projectId: ProjectId;
  modelLineageUrn: string;
  metadata?: UnknownObject;
  modelMetadata?: {
    modelFileName: string;
    modelUpdatedAt: ISO8601DateTimeString;
    modelUpdatedBy: UserId;
    latestModelVersionUrn: string;
    latestModelVersionNumber: number;
    modelRevisionNumber: number;
    modelTrashed?: boolean;
    mappedAssetCount: number;
  };
  modelSyncState?: {
    modelLineageUrn: string;
    syncState?: SyncState;
    hasMappingsNeverSynced: boolean;
    hasMappingsEditedSinceSync: boolean;
    modelMappingSyncStates: {
      assetsModelMappingId: string;
      syncState?: SyncState;
      hasBeenEditedSinceSync: boolean;
      hasNeverBeenSynced: boolean;
    }[];
  };
};

export type ModelSyncContainerDTO = Omit<
  ModelSyncContainer,
  "createdAt" | "updatedAt" | "deletedAt"
> & {
  createdAt: ISO8601DateTimeString;
  updatedAt: ISO8601DateTimeString;
  deletedAt?: ISO8601DateTimeString;
};

export const parseModel = ({
  createdAt,
  updatedAt,
  deletedAt,
  ...model
}: ModelSyncContainerDTO): ModelSyncContainer => ({
  ...model,
  createdAt: new Date(createdAt),
  updatedAt: new Date(updatedAt),
  ...(deletedAt && { deletedAt: new Date(deletedAt) }),
});

export const parseModelList = (
  models: ModelSyncContainerDTO[]
): ModelSyncContainer[] => models.map(parseModel);

export type AssetsModelMappings = {
  projectId: ProjectId;
  id: UUIDv4;
  version: number;
  isActive: boolean;
  createdAt: Date;
  createdBy: UserId;
  updatedAt: Date;
  updatedBy: UserId;
  modelSyncContainerId: ModelSyncContainer["id"];
  query: SimpleModelQueryV1;
  assetsContainer: AssetsCategoryContainer;
  modelFieldMappings: ModelFieldToAssetFieldMapping[];
  mappingMetadata?: {
    mappedAssetCount: number;
  };
};

export type ModelFieldToAssetFieldMapping = {
  entityType: "model_field_to_asset_field";
  modelFieldKey: ModelFieldDefinition["key"];
  assetFieldName: string;
  fieldSyncBehavior?: "on_entity_creation" | "on_model_version_sync";
};

export type AssetsModelMappingsDTO = Omit<
  AssetsModelMappings,
  "createdAt" | "updatedAt"
> & {
  createdAt: ISO8601DateTimeString;
  updatedAt: ISO8601DateTimeString;
};

export const parseAssetsModelMappings = ({
  createdAt,
  updatedAt,
  ...assetsModelMappings
}: AssetsModelMappingsDTO): AssetsModelMappings => ({
  ...assetsModelMappings,
  createdAt: new Date(createdAt),
  updatedAt: new Date(updatedAt),
});

export const parseAssetsModelMappingsList = (
  mappings: AssetsModelMappingsDTO[]
): AssetsModelMappings[] => mappings.map(parseAssetsModelMappings);

export type ModelFieldDefinitionsResponse = AssetsAsyncApiResponse<
  AssetsPaginatedResponse<ModelFieldDefinition[]>,
  { indexJobStatusBatch: unknown }
>;

export type ModelFieldDefinition = {
  key: string;
  category: string;
  name: string;
  type: ModelFieldType;
  uom: UnitOfMeasurement;
};

type ModelFieldType =
  | "String"
  | "LocalizableString"
  | "Integer"
  | "Double"
  | "Boolean"
  | "DateTime"
  | "GeoLocation"
  | "Position"
  | "Unknown";

export type AssetsCategoryContainer = {
  entityType: "assets_category_container";
  categoryId: Category["internalId"];
};

export type AssetModelSyncRecord = {
  id: UUIDv4;
  createdAt: Date;
  createdBy: UserId;
  updatedAt: Date;
  updatedBy: UserId;
  deletedAt?: Date;
  deletedBy?: UserId;
  isActive: boolean;
  version: number;
  projectId: ProjectId;
  modelLineageUrn: string;
  modelObjectGuid: string;
  modelExternalId: string;
  modelSvf2Id: number;
  modelLmvId: string;
  assetId: Asset["id"];
  assetsModelMappingId: AssetsModelMappings["id"];
  syncedModelVersionUrn: string;
  syncedModelVersionNumber: number;
  viewIds: string[];
  modelSyncContainerId: UUIDv4;
  detachmentMetadata?: {
    isAssetsModelMappingActive?: boolean;
    isAttachedToModel?: boolean;
    isModelSyncContainerActive?: boolean;
  };
  detachedAt?: Date;
  detachedBy?: UserId;
};

export type BaseAssetModelSyncRecord = Pick<
  AssetModelSyncRecord,
  | "modelLineageUrn"
  | "modelObjectGuid"
  | "modelExternalId"
  | "modelSvf2Id"
  | "modelLmvId"
  | "assetsModelMappingId"
  | "syncedModelVersionUrn"
  | "syncedModelVersionNumber"
  | "viewIds"
>;

export type BatchCountModelObjectsResponse = AssetsResponse<
  {
    count: number;
    mappingId: UUIDv4;
    query: SimpleModelQueryV1;
    queryId: string;
  }[]
>;

export type ModelFieldValue = string | number | boolean;

export type QueryModelObject = {
  name: string;
  modelObjectGuid: UUIDv4;
  modelExternalId: string;
  modelSvf2Id: number;
  modelSyncContainerId: string;
  modelLineageUrn: string;
  modelVersionUrn: string;
  modelVersionNumber: number;
  modelLmvId: number;
  compositeId?: number;
  descendantIds?: number[];
  detachmentMetadata?: {
    isAttachedToModel?: boolean;
    isAssetsModelMappingActive?: boolean;
    isModelSyncContainerActive?: boolean;
    assetId: Asset["id"];
    assetModelSyncRecordId: AssetModelSyncRecord["id"];
  };
  props?: Record<ModelFieldDefinition["key"], ModelFieldValue>;
};

export type QueryModelObjectsResponse = AssetsPaginatedResponse<
  QueryModelObject[]
>;

export type AssetModelSyncRecordsResponse = AssetsPaginatedResponse<
  AssetModelSyncRecord[]
>;

export const QueryItemTypes = Object.freeze([
  "$fieldEquals",
  "$fieldNotEquals",
  "$fieldSubstringSearch",
  "$fieldDoesNotContain",
  "$fieldStartsWith",
  "$fieldIsNotEmpty",
] as const);

export type QueryItemType = (typeof QueryItemTypes)[number];

export const QUERY_ITEM_TYPES_WITHOUT_VALUE = ["$fieldIsNotEmpty"];
export const QUERY_ITEM_TYPES_WITH_VALUE_AUTOCOMPLETE = [
  "$fieldEquals",
  "$fieldNotEquals",
];

export type SimpleModelQueryV1AtomicExpression = {
  [key in QueryItemType]?: {
    modelFieldKey: ModelFieldDefinition["key"];
    value: ModelFieldValue;
  };
} & {
  includedObjectTypes?: ModelObjectTypes[];
};

export type QueryType = "$and" | "$or";

export const modelObjectTypes = [
  "composite",
  "composite_child",
  "singleton",
] as const;

export type ModelObjectTypes = (typeof modelObjectTypes)[number];

export type SimpleModelQueryV1Expression = {
  [key in QueryType]?: SimpleModelQueryV1AtomicExpression[];
} & {
  includedObjectTypes?: ModelObjectTypes[];
};

export type SimpleModelQueryV1 = {
  entityType: "simple_model_query_v1";
  modelQuery: SimpleModelQueryV1Expression;
};

export type AllowedObjectTypesCombinations =
  | "includeCompositeObjects"
  | "includeCompositeChildObjects"
  | "includeAllObjects";

export type QueryBuilderState = {
  queryType: QueryType;
  queryItems: {
    queryItemType: QueryItemType;
    modelFieldKey: ModelFieldDefinition["key"];
    value: ModelFieldValue;
    objectTypesCombination?: AllowedObjectTypesCombinations;
    modelFieldType: ModelFieldDefinition["type"] | undefined;
  }[];
  objectTypesCombination?: AllowedObjectTypesCombinations;
};

export type BatchCreateAssetsModelMappingsRequest = {
  modelSyncContainerId: ModelSyncContainer["id"];
  query: SimpleModelQueryV1;
  assetsContainer: AssetsCategoryContainer;
  modelFieldMappings: ModelFieldToAssetFieldMapping[];
};

export type BatchPatchAssetsModelMappingsRequest = Record<
  AssetsModelMappings["id"],
  Partial<Pick<AssetsModelMappings, "query" | "modelFieldMappings">>
>;

export type ConflictingMapping = Pick<
  AssetsModelMappings,
  "id" | "modelSyncContainerId" | "assetsContainer"
>;

export type ConflictingModelObject = Pick<
  AssetModelSyncRecord,
  "modelObjectGuid" | "modelExternalId" | "modelSvf2Id"
> & { name: string };

export const ModelSyncActionTypes = {
  Create: "create_asset_model_sync_action",
  Update: "update_asset_model_sync_action",
  Delete: "delete_asset_model_sync_action",
  Restore: "restore_asset_model_sync_action",
  Detach: "detach_asset_model_sync_action",
  Reattach: "reattach_asset_model_sync_action",
} as const;

export type ModelSyncActionEntityType =
  (typeof ModelSyncActionTypes)[keyof typeof ModelSyncActionTypes];

export type AssetsModelMappingConflictGroup = {
  conflictingModelMappings: ConflictingMapping[];
  modelObjects: ConflictingModelObject[];
  totalModelObjects: number;
};

export type CreateAssetModelSyncAction = {
  entityType: typeof ModelSyncActionTypes.Create;
  assetModelSyncRecord: BaseAssetModelSyncRecord;
  newEntity: NewAssetDTO;
};

export type UpdateAssetModelSyncAction = {
  entityType: typeof ModelSyncActionTypes.Update;
  assetModelSyncRecord: BaseAssetModelSyncRecord;
  assetId: string;
  existingAssetName: string;
  beforeEntity: AssetDTO;
  patchEntity: Partial<AssetDTO>;
};

export type DeleteAssetModelSyncAction = {
  entityType: typeof ModelSyncActionTypes.Delete;
  assetModelSyncRecord: BaseAssetModelSyncRecord;
  assetId: string;
  existingAssetName: string;
  deletedEntity: AssetDTO;
};

export type RestoreAssetModelSyncAction = {
  entityType: typeof ModelSyncActionTypes.Restore;
  assetModelSyncRecord: BaseAssetModelSyncRecord;
  assetId: string;
  existingAssetName: string;
  beforeEntity: AssetDTO;
  patchEntity: Partial<AssetDTO>;
};

export type DetachAssetModelSyncAction = {
  entityType: typeof ModelSyncActionTypes.Detach;
  assetModelSyncRecord: BaseAssetModelSyncRecord;
  assetId: string;
  existingAssetName: string;
  detachedEntity: AssetDTO;
};

export type ReattachAssetModelSyncAction = {
  entityType: typeof ModelSyncActionTypes.Reattach;
  assetModelSyncRecord: BaseAssetModelSyncRecord;
  assetId: string;
  existingAssetName: string;
  beforeEntity: AssetDTO;
  patchEntity: Partial<AssetDTO>;
};

export type ModelSyncAction =
  | CreateAssetModelSyncAction
  | UpdateAssetModelSyncAction
  | DeleteAssetModelSyncAction
  | RestoreAssetModelSyncAction
  | DetachAssetModelSyncAction
  | ReattachAssetModelSyncAction;

export type ModelAssetDiffs = ModelSyncAction[] & {
  counts: Omit<
    ModelAssetDiffCounts,
    "assetsModelMappingId" | "modelSyncContainerId"
  >;
};

export type ModelAssetDiffCounts = {
  assetsModelMappingId: AssetsModelMappings["id"];
  modelSyncContainerId: ModelSyncContainer["id"];
  totalCreated: number;
  totalUpdated: number;
  totalDeleted: number;
  totalRestored: number;
  totalDetached: number;
  totalReattached: number;
};

export const REASON_CODE_NO_3D_GEOMETRY = "FILE_HAS_NO_3D_GEOMETRY";
export const REASON_CODE_NO_SVF2_OUTPUTS = "FILE_HAS_NO_SVF2_OUTPUTS";

export const unsupportedReasonCodes = Object.freeze([
  REASON_CODE_NO_3D_GEOMETRY,
  REASON_CODE_NO_SVF2_OUTPUTS,
] as const);

export type UnsupportedReasonCode = (typeof unsupportedReasonCodes)[number];

export const STABLE_IDS_NOT_SUPPORTED = "STABLE_IDS_NOT_SUPPORTED";
export const ERROR_CHECKING_FILE_STATUS = "ERROR_CHECKING_FILE_STATUS";

export const warningCodes = Object.freeze([
  STABLE_IDS_NOT_SUPPORTED,
  ERROR_CHECKING_FILE_STATUS,
] as const);

export type WarningCode = (typeof warningCodes)[number];

export type ModelProcessingStatus = {
  modelSyncContainerId: ModelSyncContainer["id"];
  latestModelVersionUrn: string;
  status:
    | "processing"
    | "success"
    | "unsupported"
    | "failed"
    | "timeout"
    | "suspended";
  unsupportedReasonCodes?: UnsupportedReasonCode[];
  warningCodes?: WarningCode[];
  progress: string;
};

export const TYPES_FOR_FIELD_SUBSTRING_SEARCH = ["String", "LocalizableString"];

export type ModelSyncRecordsQueryOptions = {
  modelSyncContainerIds?: ModelSyncContainer["id"][];
  assetsModelMappingIds?: AssetsModelMappings["id"][];
  assetIds?: Asset["id"][];
  attachedStates?: ["attached" | "detached"];
  cursorState?: CursorState;
  limit?: number;
  includeDeleted?: boolean;
  includeViews?: boolean;
  includeDetachmentMetadata?: boolean;
};
